import React, {
 memo, useCallback, useEffect, useRef, useState
} from 'react';
import { combineClassNames } from '@utils/combineClassNames';
import { TRIAL_WORDMARK, trialDemoVideoUrl } from './trial-media';

const TELLA_ORIGIN = 'https://www.tella.tv';

function tellaMessageType(data: unknown): string | null {
  const payload = typeof data === 'string'
    ? (() => {
      try {
        return JSON.parse(data);
      } catch {
        return null;
      }
    })()
    : data;

  if (!payload || typeof payload !== 'object') return null;

  const { type } = (payload as { type?: unknown });

  return typeof type === 'string' ? type : null;
}

type Props = {
  languageLocale?: string;
  title: string;
  watchLabel: string;
  demoLabel: string;
};

export const TrialDemoPlayer = memo(({
  languageLocale, title, watchLabel, demoLabel,
}: Props) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const src = trialDemoVideoUrl(languageLocale);

  const sendPlay = useCallback(() => {
    iframeRef.current?.contentWindow?.postMessage({ type: 'play' }, TELLA_ORIGIN);
  }, []);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== TELLA_ORIGIN) return;

      const type = tellaMessageType(event.data);

      if (type === 'ready') setIsReady(true);
      if (type === 'play' || type === 'playing') setIsPlaying(true);
    };

    window.addEventListener('message', onMessage);

    return () => window.removeEventListener('message', onMessage);
  }, []);

  useEffect(() => {
    if (isPlaying && isReady) sendPlay();
  }, [isPlaying, isReady, sendPlay]);

  return (
    <div className={combineClassNames('FreeTrial__Build__Video', {
      'FreeTrial__Build__Video--playing': isPlaying,
    })}
    >
      <iframe
        ref={iframeRef}
        className="FreeTrial__Build__Player"
        src={src}
        title={title}
        allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
        allowFullScreen
      />
      {!isPlaying && (
        <div className="FreeTrial__Build__VideoPoster">
          <svg className="FreeTrial__Build__VideoBg" viewBox="0 0 800 450" preserveAspectRatio="xMidYMid slice">
            <circle cx="700" cy="40" r="260" fill="#2463eb" fillOpacity=".35" />
            <circle cx="80" cy="460" r="220" fill="#4f39f6" fillOpacity=".3" />
            <path
              d="M0 320C160 280 300 360 460 320C620 280 700 330 800 300"
              stroke="#c7d2fe"
              strokeOpacity=".2"
              strokeWidth="2"
              fill="none"
            />
          </svg>
          <img src={TRIAL_WORDMARK} alt="" />
          <button
            type="button"
            className="FreeTrial__Build__Play"
            aria-label={title}
            onClick={() => {
              setIsPlaying(true);
              sendPlay();
            }}
          >
            <svg width="38" height="38" viewBox="0 0 24 24" fill="#2463eb"><path d="M8 5l12 7-12 7z" /></svg>
          </button>
          <div className="FreeTrial__Build__VideoCopy">
            <strong>{watchLabel}</strong>
            <b>{demoLabel}</b>
          </div>
        </div>
      )}
    </div>
  );
});
